/**
 * WebSocket message dispatcher.
 *
 * Routes incoming messages by ``message_type`` to the appropriate
 * handler: React Query cache updates, Zustand store mutations, or
 * toast notifications.
 */

import { toast } from 'sonner';
import { queryClient } from '@/store/queryClient';
import { useUploadStore } from '@/store/uploadStore';
import type { WsMessage } from './wsMessages';

/**
 * Central message dispatcher.  Call this from the ``onMessage``
 * callback of your WsClient instances.
 */
export function handleWsMessage(data: Record<string, unknown>): void {
  const msg = data as unknown as WsMessage;

  switch (msg.message_type) {
    // -----------------------------------------------------------------
    // Analysis pipeline
    // -----------------------------------------------------------------
    case 'analysis_progress':
      queryClient.setQueryData(
        ['analysis-status', msg.event_id],
        (old: Record<string, unknown> | undefined) => {
          if (!old) return old;
          return {
            ...old,
            current_stage: msg.stage,
            status: 'running',
            progress: {
              ...(old.progress as Record<string, unknown>),
              processed_photos: msg.completed,
              total_photos: msg.total,
              percentage: msg.percentage,
            },
          };
        },
      );
      break;

    case 'analysis_complete':
      // Invalidate queries so components refetch fresh data
      queryClient.invalidateQueries({ queryKey: ['analysis-status', msg.event_id] });
      if (msg.photo_id) {
        queryClient.invalidateQueries({ queryKey: ['photo', msg.photo_id] });
      }
      queryClient.invalidateQueries({ queryKey: ['photos'] });
      queryClient.invalidateQueries({ queryKey: ['event', msg.event_id] });
      break;

    case 'analysis_failed':
      queryClient.invalidateQueries({ queryKey: ['analysis-status', msg.event_id] });
      toast.error(`Analysis failed${msg.stage ? ` at ${msg.stage}` : ''}: ${msg.error}`);
      break;

    // -----------------------------------------------------------------
    // Upload
    // -----------------------------------------------------------------
    case 'upload_progress': {
      const store = useUploadStore.getState();
      if (msg.file_id) {
        store.updateItemProgress(
          msg.file_id,
          msg.progress,
          msg.uploaded_bytes,
        );
      }
      break;
    }

    case 'upload_complete':
      queryClient.invalidateQueries({ queryKey: ['photos'] });
      toast.success(`Upload complete: ${msg.total_files} file(s)`);
      break;

    case 'upload_failed':
      if (msg.file_id) {
        useUploadStore.getState().markItemFailed(msg.file_id, msg.error);
      }
      toast.error(`Upload failed: ${msg.error}`);
      break;

    // -----------------------------------------------------------------
    // Face clustering
    // -----------------------------------------------------------------
    case 'face_clustering_complete':
      queryClient.invalidateQueries({ queryKey: ['faces'] });
      queryClient.invalidateQueries({ queryKey: ['event', msg.event_id] });
      toast.success(`Face clustering done: ${msg.cluster_count} cluster(s)`);
      break;

    // -----------------------------------------------------------------
    // Similarity
    // -----------------------------------------------------------------
    case 'similarity_complete':
      queryClient.invalidateQueries({ queryKey: ['similarity'] });
      queryClient.invalidateQueries({ queryKey: ['event', msg.event_id] });
      toast.success(`Similarity analysis done: ${msg.group_count} group(s)`);
      break;

    // -----------------------------------------------------------------
    // Generic notification
    // -----------------------------------------------------------------
    case 'notification':
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      toast(msg.title);
      break;

    default:
      // Unknown message type -- silently ignore
      break;
  }
}
